'use client';

import { useCallback, useMemo } from 'react';
import { encodeFunctionData, type Hex } from 'viem';
import { Coins } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { PROTECTION_REDEEMER_ABI } from '@takeover/sdk/contracts';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { formatUSD } from '@/lib/utils';
import { useTransactionQueue } from '@/hooks/use-transactions';
import { useWallet } from '@/hooks/use-wallet';
import {
  HV_KEEPER_FEE_ADAPTER_ADDRESS,
  PROTECTION_REDEEMER_ADDRESS,
  TAKEOVER_TOKEN_ADDRESS,
} from '@/lib/constants/addresses';
import { LobbyYieldPanelActionButton } from './lobby-yield-panel-action';

type Rail = '7710-direct' | '7715-grant';

/** V2 ProtectionRedeemer's per-row tuple, wire-shape. */
interface RedemptionItem {
  ugm: string;
  gridId: string;
  seatId: string;
  taxToken: string;
  /** Decimal string in base units. */
  principal: string;
  /** Decimal string in base units. */
  fee: string;
}

interface RedemptionRow {
  actionId: number;
  holder: string;
  ugm: string;
  gridId: string;
  seatId: number;
  taxToken: string;
  principal: string;
  fee: string;
  feeUsd: number;
  runwaySecondsAtQueue: number;
  rail: Rail;
  permissionContext: Hex;
  mode: Hex;
  executionCallData: Hex;
  /** Present only on `7715-grant` rows; required by V2's `redeem(items, …)`. */
  item?: RedemptionItem;
}

interface RailBucket {
  count: number;
  redemptions: RedemptionRow[];
  totals: {
    usdcMicros: string;
    takeoverWei: string;
    otherCount: number;
  };
}

interface KeeperFeesResponse {
  count: number;
  pendingTotalUsd: number;
  totals: {
    usdcMicros: string;
    takeoverWei: string;
    otherCount: number;
  };
  redemptions: RedemptionRow[];
  rails: {
    '7710-direct': RailBucket;
    '7715-grant': RailBucket;
  };
}

const KEEPER_QUERY_KEY = ['boardroom-keeper-fees'] as const;

const collectTooltipSurface =
  'max-w-[260px] rounded-[6px] border border-emerald-500/28 bg-black/95 backdrop-blur-md shadow-lg px-2.5 py-2 text-[10px] leading-snug text-emerald-50/95 tracking-[0.06em]';

/**
 * Boardroom-side "Collect Fees" button rendered inside the keeper-fee
 * adapter's Custom panel row. Surfaces the queued top-up volume and
 * lets anyone (no boardroom-seat gate) sweep them in a single
 * `BoardroomKeeperFeeAdapter.redeemAndFlush(...)` transaction.
 *
 * The 1% keeper fee accumulates inside the adapter; USDC fees are
 * pushed to the boardroom grid as yield in the same tx, while
 * non-USDC fees (TAKEOVER, ETH, etc.) sit until the adapter's
 * `owner()` claims them via Etherscan.
 *
 * UI rules:
 *   - Disabled when wallet not connected (tooltip explains).
 *   - Disabled when nothing is pending or pending volume is too
 *     small to render meaningfully ($1 floor — same threshold as the
 *     protocol "Collect Taxes" button).
 *   - Label mirrors the adjacent `CollectButton`'s tone: `Collect Fees`
 *     when idle, `Collect Fees: +$X` when the queue has volume.
 */
export function BoardroomKeeperCollectFees() {
  const adapterAddress = (HV_KEEPER_FEE_ADAPTER_ADDRESS || '').trim();
  const configured = adapterAddress.length > 0;
  const { wallet } = useWallet();
  const walletConnected = configured && !!wallet;
  const { executeActions } = useTransactionQueue();
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery<KeeperFeesResponse>({
    queryKey: KEEPER_QUERY_KEY,
    enabled: configured,
    queryFn: async () => {
      const res = await fetch('/api/boardroom/keeper-fees', { cache: 'no-store' });
      if (!res.ok) throw new Error(`keeper-fees: HTTP ${res.status}`);
      return (await res.json()) as KeeperFeesResponse;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const breakdown = useMemo(() => {
    if (!data) return null;
    const usdcDollars = Number(BigInt(data.totals.usdcMicros)) / 1e6;
    const takeoverTokens = Number(BigInt(data.totals.takeoverWei)) / 1e18;
    return { usdcDollars, takeoverTokens, otherCount: data.totals.otherCount };
  }, [data]);

  const onSuccess = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: KEEPER_QUERY_KEY });
    // The adapter pushes to the boardroom grid as yield in the same tx,
    // so refetch the protocol panel's collectable + the live grid.
    void queryClient.invalidateQueries({ queryKey: ['hv-collectable-fees'] });
    void queryClient.invalidateQueries({ queryKey: ['boardroom-api-data'] });
  }, [queryClient]);

  const handleCollect = useCallback(async () => {
    if (!data || data.count === 0 || !PROTECTION_REDEEMER_ADDRESS) return;

    // Single onchain entrypoint for BOTH rails now: `ProtectionRedeemer.
    // redeem(items, …)` in SINGLE mode (pull-then-compose). 7715 grants
    // name the redeemer as their delegate; 7710 delegations use
    // ANY_DELEGATE, which lets the redeemer be `msg.sender` too — so both
    // batch into one call. (The legacy 7710 path went through
    // `BKFA.redeemAndFlush` with a BATCH execution, which the spend-cap
    // enforcer rejected with `CaveatEnforcer:invalid-call-type`.)
    //
    // Every row carries the per-row `item` tuple the redeemer needs (the
    // executionCallData is just `transfer(redeemer, total)`). A row
    // missing `item` is a server-side bug; drop it client-side rather
    // than letting `encodeFunctionData` throw on undefined.
    const rows = [
      ...data.rails['7710-direct'].redemptions,
      ...data.rails['7715-grant'].redemptions,
    ].filter((r): r is RedemptionRow & { item: RedemptionItem } => !!r.item);
    if (rows.length === 0) return;

    const directCount = data.rails['7710-direct'].redemptions.filter((r) => !!r.item).length;
    const grantCount = data.rails['7715-grant'].redemptions.filter((r) => !!r.item).length;
    const descParts: string[] = [];
    if (directCount > 0) descParts.push(directCount === 1 ? '1 direct-signed seat' : `${directCount} direct-signed seats`);
    if (grantCount > 0) descParts.push(grantCount === 1 ? '1 MetaMask Smart Account seat' : `${grantCount} MetaMask Smart Account seats`);
    const calls = [
      {
        id: 'redeem-redeemer',
        label: 'Protecting Land and sending fees to the Boardroom',
        description: `Topping up ${descParts.join(' and ')}.`,
        call: {
          to: PROTECTION_REDEEMER_ADDRESS as Hex,
          data: encodeFunctionData({
            abi: PROTECTION_REDEEMER_ABI,
            functionName: 'redeem',
            args: [
              rows.map((r) => ({
                ugm: r.item.ugm as Hex,
                gridId: BigInt(r.item.gridId),
                seatId: BigInt(r.item.seatId),
                taxToken: r.item.taxToken as Hex,
                principal: BigInt(r.item.principal),
                fee: BigInt(r.item.fee),
              })),
              rows.map((r) => r.permissionContext),
              rows.map((r) => r.mode),
              rows.map((r) => r.executionCallData),
            ],
          }),
        },
      },
    ];

    const { success, results } = await executeActions('Collect Keeper Fees', calls, {
      onSuccess,
    });

    // Deterministic settlement: the moment the redeem tx confirms, tell
    // the server which actionIds it swept (with the real txHash) so the
    // queue clears and the holders' Protection History rows appear
    // immediately, rather than waiting on the indexer-backed reconcile
    // cron. Best-effort — a settle failure just defers to reconcile.
    if (success) {
      const stepResult = results.get('redeem-redeemer') as { hash?: string } | undefined;
      const settleTxHash = stepResult?.hash;
      const actionIds = rows.map((r) => Number(r.actionId));
      if (settleTxHash && settleTxHash !== '0x' && actionIds.length > 0) {
        try {
          await fetch('/api/boardroom/keeper-fees/settle', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ txHash: settleTxHash, actionIds }),
          });
        } catch {
          // Swallow — reconcile is the durable backstop.
        }
      }
      // Reflect the now-`executed` rows (button clears) once settled.
      void queryClient.invalidateQueries({ queryKey: KEEPER_QUERY_KEY });
    }
  }, [data, executeActions, onSuccess, queryClient]);

  if (!configured) return null;

  const pendingUsd = data?.pendingTotalUsd ?? 0;
  const pendingCount = data?.count ?? 0;
  // ≥ $0.01 of fundable fees — the floor below which a sweep isn't worth the
  // gas. Sub-cent dust and underfunded (server-dropped) top-ups never make
  // the button actionable and are NOT surfaced here: a skipped top-up skips
  // silently — the affected tile owner is warned in their own Land
  // Protection panel, which is the surface that matters.
  const collectable = pendingCount > 0 && pendingUsd >= 0.01;
  const canCollect = walletConnected && collectable;

  // Headline matches the protocol Collect Taxes button: idle, or "+$1.23"
  // once there's something genuinely worth collecting. No "N pending" state.
  const label = (() => {
    if (isPending && !data) return 'Loading…';
    if (collectable) return `Collect Fees: +${formatUSD(pendingUsd)}`;
    return 'Collect Fees';
  })();

  const tooltipBody = (() => {
    if (!walletConnected) {
      return 'Connect your wallet to sweep pending Deposit-Protection top-ups into the boardroom.';
    }
    // Speak up ONLY when there's something actually collectable. No chatter
    // for sub-cent dust or skipped/underfunded rows — the button is either a
    // real call to action or silent.
    if (!collectable || !breakdown) return null;
    const { usdcDollars, takeoverTokens, otherCount } = breakdown;
    const lines = [`${pendingCount} top-up${pendingCount === 1 ? '' : 's'} ready.`];
    if (usdcDollars > 0) lines.push(`${formatUSD(usdcDollars)} USDC fees → boardroom yield.`);
    if (takeoverTokens > 0) {
      const takeoverLabel = TAKEOVER_TOKEN_ADDRESS ? 'TAKEOVER' : 'governance token';
      lines.push(`${takeoverTokens.toFixed(2)} ${takeoverLabel} → claimable by guardian.`);
    }
    if (otherCount > 0) lines.push(`${otherCount} non-USDC fee${otherCount === 1 ? '' : 's'} → claimable by guardian.`);
    return lines.join(' ');
  })();

  const button = (
    <LobbyYieldPanelActionButton
      tone="emerald"
      icon={Coins}
      label={label}
      labelClassName="normal-case tracking-[0.04em] whitespace-nowrap tabular-nums flex-1"
      disabled={!canCollect}
      pending={false}
      onClick={handleCollect}
    />
  );

  if (!tooltipBody) return button;

  return (
    <Tooltip disableTouch>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex w-full">
          {button}
        </span>
      </TooltipTrigger>
      <TooltipContent sideOffset={6} className={collectTooltipSurface}>
        {tooltipBody}
      </TooltipContent>
    </Tooltip>
  );
}
